import { useDocItems } from '@modules/docs/providers';
import {
  IconButton,
  List,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { DocItem } from 'src/@types/docs';
import { CustomTextField } from 'src/components/custom-input';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { levenshteinDistance } from 'src/utils/stringUtils';

const docItem = {
  _id: 'root',
  title: 'Docs',
  order: 0,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

type Prop = {
  selectDoc: (doc: DocItem) => void;
  backToRoot: () => void;
  selectedArray: string[];
};

export const AddDocSelector = ({
  selectDoc,
  backToRoot,
  selectedArray = [],
}: Prop) => {
  const [selectedRoot, setSelectedRoot] = useState<DocItem>(docItem);
  const [previousRoot, setPreviousRoot] = useState<DocItem[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { translate } = useLocales();
  const { docs, isDocsLoading } = useDocItems();

  const docChildren = useMemo(() => {
    if (!docs?.length) return [];
    if (selectedRoot._id === 'root') {
      return docs.filter((item) => !item.parent && !item.noteRef);
    }
    return docs.filter((item) => item.parent === selectedRoot._id);
  }, [docs, selectedRoot._id]);

  const hasDocChildren = (doc: DocItem) => {
    if (!docs?.length) return false;

    return docs.filter((item) => item.parent === doc._id).length;
  };

  const searchedDocs = useMemo(() => {
    if (!searchTerm || !docs?.length) return [];

    const searchTerms = searchTerm.toLowerCase().trim().split(/\s+/);

    return docs.filter((item) => {
      const title = item.title.toLowerCase();
      return searchTerms.some((term) => {
        if (title.indexOf(term) !== -1) return true;

        const words = title.split(/\s+/);

        return words.some((word) => {
          const distance = levenshteinDistance(term, word);
          const maxDistance = term.length <= 3 ? 1 : 2;
          return distance <= maxDistance;
        });
      });
    });
  }, [searchTerm, docs]);

  const docsList = useMemo(
    () => (searchTerm ? searchedDocs : docChildren),
    [searchedDocs, searchTerm, docChildren],
  );

  const isSelected = useCallback(
    (doc: DocItem) => selectedArray.includes(doc._id),
    [selectedArray],
  );

  const handleSelectRoot = (e: React.MouseEvent, doc: DocItem) => {
    e.stopPropagation();
    setPreviousRoot((oldValue) => [...oldValue, selectedRoot]);
    setSelectedRoot(doc);
  };

  const handleBack = () => {
    const lastItem = previousRoot[previousRoot.length - 1];
    if (previousRoot.length) {
      const newPrevious = previousRoot;
      newPrevious.pop();
      setPreviousRoot(newPrevious);
      setSelectedRoot(lastItem);
      return;
    }
    backToRoot();
  };

  return (
    <List className="sublist">
      <Stack direction="row" alignItems="center" gap={1} mb={2}>
        <IconButton onClick={handleBack}>
          <Iconify icon="tabler:arrow-left" />
        </IconButton>

        <StyledInput
          InputProps={{
            startAdornment: <Iconify icon="tabler:search" />,
          }}
          placeholder={translate('shared.search.find_a_doc')}
          fullWidth
          className={'input'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Stack>
      <List className="sublist">
        {searchTerm && !searchedDocs.length && (
          <Typography variant="caption" color="textSecondary" component="div">
            {translate('shared.not_found.no_docs_found')}
          </Typography>
        )}
        {docsList.map((doc) => (
          <MenuItem
            key={doc._id}
            disabled={isDocsLoading}
            onClick={() => selectDoc(doc)}
          >
            <ListItemIcon>
              {doc.icon ? (
                <em-emoji shortcodes={doc.icon.value} />
              ) : (
                <Iconify icon="tabler:file-description" />
              )}
            </ListItemIcon>
            <ListItemText>{doc.title}</ListItemText>
            {isSelected(doc) && (
              <Iconify icon="tabler:check" color="success.main" />
            )}
            {!!hasDocChildren(doc) && (
              <StyledNavigateIcon
                icon="tabler:chevron-right"
                ml={1}
                onClick={(e) => handleSelectRoot(e, doc)}
              />
            )}
          </MenuItem>
        ))}
      </List>
    </List>
  );
};

const StyledInput = styled(CustomTextField)(({ theme }) => ({
  mb: theme.spacing(1),
  '&.input': {
    '& .MuiInputBase-input': {
      padding: `${theme.spacing(1)} !important`,
    },
  },
}));

const StyledNavigateIcon = styled(Iconify)(({ theme }) => ({
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.text.primary,
  },
}));
